import { useState, useEffect, useMemo, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  getAppointments,
  createAppointment,
  updateAppointment,
  deleteAppointment,
  getCustomers,
  getServices,
  getBranches,
} from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import SelectBox from "../components/UI/SelectBox";
import ConfirmModal from "../components/UI/ConfirmModal";
import { notify } from "../utils/notify";
import PageHeader from "../components/UI/PageHeader";
import { I18nContext, useTranslation } from "../context/I18nContext";
import { AuthContext } from "../context/AuthContext";

// --- Calendar geometry --------------------------------------------------
// A fixed 08:00-22:00 day window in 30-minute slots is a plain constant
// (not configurable) — reasonable default hours for a salon/beauty-center
// business, and keeping it fixed keeps the whole grid's math simple. Every
// appointment block is positioned with plain top/height math off these
// constants rather than a real calendar library — no such dependency
// exists in this project yet, and the interaction this needs (click an
// empty slot to book, click a block to edit) doesn't call for one.
const SLOT_MINUTES = 30;
const DAY_START_HOUR = 8;
const DAY_END_HOUR = 22;
const SLOT_HEIGHT = 48; // px per SLOT_MINUTES
const DAY_START_MINUTES = DAY_START_HOUR * 60;
const TOTAL_SLOTS = ((DAY_END_HOUR - DAY_START_HOUR) * 60) / SLOT_MINUTES;

const pad = (n) => String(n).padStart(2, "0");
const dateToISODate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const timeLabel = (mins) => `${pad(Math.floor(mins / 60))}:${pad(mins % 60)}`;

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

// toRFC3339Local turns a Date (read in the BROWSER's own local timezone)
// into a proper RFC3339 string carrying that same offset explicitly —
// never .toISOString(), which silently converts to UTC and would send the
// wrong wall-clock hour to a server whose own timezone differs from the
// browser's. See ListAppointmentsHandler's own comment (appointment_handler.go)
// on why the two sides need to agree on this.
function toRFC3339Local(date) {
  const offsetMin = -date.getTimezoneOffset();
  const sign = offsetMin >= 0 ? "+" : "-";
  const offH = pad(Math.floor(Math.abs(offsetMin) / 60));
  const offM = pad(Math.abs(offsetMin) % 60);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:00${sign}${offH}:${offM}`;
}

const STATUS_COLORS = {
  scheduled: "bg-blue-50 text-blue-700 border-blue-200",
  completed: "bg-green-50 text-green-700 border-green-200",
  cancelled: "bg-gray-100 text-gray-400 border-gray-200 line-through",
  no_show: "bg-red-50 text-red-600 border-red-200",
};

export default function Appointments() {
  const { languageCode } = useContext(I18nContext);
  const { userProfile, userPermissions } = useContext(AuthContext);
  const { t } = useTranslation("appointments");
  const { t: tCommon } = useTranslation("common");
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [date, setDate] = useState(() => new Date());
  const [appointments, setAppointments] = useState([]);
  const [services, setServices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [staffFilter, setStaffFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null); // { mode: "create"|"edit", appointment?, staffId?, startTime? }
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, id: null });
  const [deleteStatus, setDeleteStatus] = useState("idle");
  // dragPreview is the only drag-and-drop state that triggers a re-render —
  // { appointmentId, staffId, top, height } for the ghost block shown while
  // dragging, or null when nothing is being dragged. Everything else about
  // an in-progress drag lives in refs below (see the drag-and-drop block
  // near the bottom of this component) so fast mousemove events never
  // thrash React's render cycle.
  const [dragPreview, setDragPreview] = useState(null);

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const data = await getAppointments(token, { date: dateToISODate(date) });
      setAppointments(data || []);
    } catch (err) {
      notify.error(t("notify_load_failed", "Failed to load appointments"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAppointments(); }, [date, languageCode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getServices(token).then(setServices).catch(() => notify.error(t("notify_load_services_failed", "Failed to load services")));
    getCustomers(token).then(setCustomers).catch(() => notify.error(t("notify_load_customers_failed", "Failed to load customers")));
    getBranches(token).then(setBranches).catch(() => {});
  }, [languageCode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Staff shown as calendar columns = every user with at least one ACTIVE
  // assignment to an ACTIVE service — derived from the same services list
  // the booking modal's own Service dropdown uses, rather than a separate
  // "every user" call that would also drag in cashiers/admins who never
  // perform a service at all.
  const staffList = useMemo(() => {
    const map = new Map();
    for (const s of services) {
      if (!s.is_active) continue;
      for (const a of s.staff_assignments || []) {
        if (a.is_active && a.user) map.set(a.user.id, a.user);
      }
    }
    return Array.from(map.values()).sort((a, b) => (a.username || "").localeCompare(b.username || ""));
  }, [services]);

  const visibleStaff = staffFilter ? staffList.filter((u) => String(u.id) === staffFilter) : staffList;

  const staffFilterOptions = [
    { value: "", label: t("filter_all_staff", "All staff") },
    ...staffList.map((u) => ({ value: String(u.id), label: u.job_title ? `${u.username} — ${u.job_title}` : u.username })),
  ];

  const appointmentsByStaff = useMemo(() => {
    const map = new Map();
    for (const a of appointments) {
      const list = map.get(a.user_id) || [];
      list.push(a);
      map.set(a.user_id, list);
    }
    return map;
  }, [appointments]);

  const slots = useMemo(() => {
    const arr = [];
    for (let i = 0; i < TOTAL_SLOTS; i++) arr.push(DAY_START_MINUTES + i * SLOT_MINUTES);
    return arr;
  }, []);

  // canManage mirrors the backend's own canManageAppointmentFor
  // (appointment_handler.go) — a UX aid so a regular staff member sees a
  // read-only view / no drag handle for appointments they can't touch
  // instead of a confusing failed save, NOT the actual enforcement
  // boundary (that's the backend's job, on every create/update/delete
  // call regardless of what this returns). Three tiers, same as the
  // backend: you may manage your OWN appointments; a branch's designated
  // Manager may manage every appointment at that branch (branches[].manager_id,
  // matched against the signed-in user); "all_branches_access" bypasses both.
  const canManage = (targetUserId, targetBranchId) => {
    if (!userProfile) return false;
    if (Number(userProfile.id) === Number(targetUserId)) return true;
    if (userPermissions?.includes("all_branches_access")) return true;
    if (targetBranchId) {
      const branch = branches.find((b) => b.id === Number(targetBranchId));
      if (branch && branch.manager_id && Number(branch.manager_id) === Number(userProfile.id)) return true;
    }
    return false;
  };

  const openNewAppointment = (staffId, slotMinutes) => {
    const start = new Date(date);
    start.setHours(Math.floor(slotMinutes / 60), slotMinutes % 60, 0, 0);
    setModal({ mode: "create", staffId, startTime: start });
  };

  const openEditAppointment = (appointment) => setModal({ mode: "edit", appointment });
  const closeModal = () => setModal(null);
  const handleSaved = () => {
    closeModal();
    fetchAppointments();
  };

  const handleDeleteClick = (id) => setDeleteModal({ isOpen: true, id });
  const confirmDelete = async () => {
    try {
      setDeleteStatus("loading");
      await deleteAppointment(token, deleteModal.id);
      setDeleteStatus("success");
      setTimeout(() => {
        setDeleteModal({ isOpen: false, id: null });
        setDeleteStatus("idle");
      }, 1000);
      notify.success(t("notify_deleted", "Appointment deleted"));
      closeModal();
      fetchAppointments();
    } catch (err) {
      setDeleteStatus("error");
      notify.error(err.data?.error || err.message || t("notify_delete_failed", "Failed to delete appointment"));
    }
  };

  // --- Drag-and-drop rescheduling -----------------------------------
  // Dragging a SCHEDULED block (see beginDrag's own status check below —
  // a cancelled/completed/no-show booking is history, not draggable) sends
  // the SAME start_time/user_id fields the edit modal's own Save button
  // would, through the same PUT /appointments/:id endpoint — there's no
  // separate "reschedule" API. The backend's own overlap check
  // (hasOverlappingAppointment in appointment_handler.go) stays the single
  // source of truth for whether a drop is actually valid, exactly as it is
  // for a normal edit; the frontend doesn't duplicate that logic. An
  // invalid drop (double-booking the target staff, or dropping onto a
  // staff member not assigned to this service) simply comes back as a
  // 409/400 shown via notify.error — nothing is ever mutated
  // optimistically, so a rejected drop just leaves the real appointment
  // exactly where it was; dragRef/dragPreview reset unconditionally either
  // way.
  const dragRef = useRef(null);
  // Sits just outside React state: mouseup clears dragRef synchronously
  // (before the browser's own following "click" event fires), so onClick
  // can't read dragRef itself to tell a real drag apart from a plain
  // click. This ref survives that one extra tick so onClick can check it.
  const lastDragMovedRef = useRef(false);
  // staffId -> the column's own slots DOM node, used during a drag to work
  // out which staff column the cursor is currently over (by comparing
  // clientX against each column's own getBoundingClientRect — this reads
  // real rendered geometry, so it's correct in RTL just as much as LTR).
  const columnRefs = useRef({});

  const commitReschedule = async (appointment, staffId, topPx) => {
    const newStartMins = DAY_START_MINUTES + (topPx / SLOT_HEIGHT) * SLOT_MINUTES;
    const newStart = new Date(date);
    newStart.setHours(Math.floor(newStartMins / 60), newStartMins % 60, 0, 0);
    const payload = {
      customer_id: appointment.customer_id,
      service_id: appointment.service_id,
      user_id: staffId,
      branch_id: appointment.branch_id || 0,
      start_time: toRFC3339Local(newStart),
      notes: appointment.notes || "",
      status: appointment.status,
    };
    try {
      await updateAppointment(token, appointment.id, payload);
      notify.success(t("notify_rescheduled", "Appointment rescheduled"));
      fetchAppointments();
    } catch (err) {
      notify.error(err.data?.error || err.message || t("notify_reschedule_failed", "Failed to reschedule appointment"));
    }
  };
  // The mousemove/mouseup listeners below are attached once on mount, so a
  // closure captured there would go stale the moment the viewed `date`
  // changes. Routing every commit through this ref (refreshed every
  // render) keeps it always calling the current commitReschedule instead.
  const commitRescheduleRef = useRef(commitReschedule);
  commitRescheduleRef.current = commitReschedule;

  const beginDrag = (appointment) => (e) => {
    if (appointment.status !== "scheduled" || !canManage(appointment.user_id, appointment.branch_id)) return;
    const start = new Date(appointment.start_time);
    const startMins = start.getHours() * 60 + start.getMinutes();
    const origTop = ((startMins - DAY_START_MINUTES) / SLOT_MINUTES) * SLOT_HEIGHT;
    const durMins = Math.max(15, (new Date(appointment.end_time) - start) / 60000);
    const height = (durMins / SLOT_MINUTES) * SLOT_HEIGHT;
    dragRef.current = {
      appointment,
      origStaffId: appointment.user_id,
      origTop,
      height,
      startX: e.clientX,
      startY: e.clientY,
      staffId: appointment.user_id,
      top: origTop,
      moved: false,
    };
  };

  useEffect(() => {
    const handleMove = (e) => {
      const drag = dragRef.current;
      if (!drag) return;
      const deltaX = e.clientX - drag.startX;
      const deltaY = e.clientY - drag.startY;
      // A small movement threshold before treating this as a real drag —
      // otherwise every plain click would jitter by a pixel or two and
      // never reach openEditAppointment.
      if (!drag.moved && Math.hypot(deltaX, deltaY) > 4) {
        drag.moved = true;
        document.body.style.userSelect = "none";
      }
      if (!drag.moved) return;

      let staffId = drag.staffId;
      for (const [id, el] of Object.entries(columnRefs.current)) {
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (e.clientX >= rect.left && e.clientX <= rect.right) {
          staffId = Number(id);
          break;
        }
      }
      const maxTop = Math.max(0, TOTAL_SLOTS * SLOT_HEIGHT - drag.height);
      const snappedTop = Math.min(
        Math.max(0, Math.round((drag.origTop + deltaY) / SLOT_HEIGHT) * SLOT_HEIGHT),
        maxTop
      );

      drag.staffId = staffId;
      drag.top = snappedTop;
      setDragPreview({ appointmentId: drag.appointment.id, staffId, top: snappedTop, height: drag.height });
    };

    const handleUp = () => {
      const drag = dragRef.current;
      dragRef.current = null;
      document.body.style.userSelect = "";
      setDragPreview(null);
      lastDragMovedRef.current = !!drag?.moved;
      if (!drag || !drag.moved) return;
      if (drag.staffId === drag.origStaffId && drag.top === drag.origTop) return;
      commitRescheduleRef.current(drag.appointment, drag.staffId, drag.top);
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, []);

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto transition-all duration-300">
      <PageHeader className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4 border-b border-gray-100 pb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
          </button>
          <div className="flex items-center justify-center min-w-[48px] h-[48px] text-[var(--primary-color)]">
            <ICONS.Appointments size={40} className="shrink-0" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>{t("page_title", "Appointments")}</h2>
            <p className="text-gray-400 text-xs md:text-sm font-medium">{t("page_subtitle", "The booking calendar — the actual sale still happens at Checkout when the customer arrives")}</p>
          </div>
        </div>

        <Button
          variant="primary"
          icon={ICONS.Plus}
          onClick={() => setModal({ mode: "create", staffId: null, startTime: (() => { const d = new Date(date); d.setHours(DAY_START_HOUR, 0, 0, 0); return d; })() })}
          className="w-full sm:w-auto shrink-0"
        >
          {t("btn_new_appointment", "New Appointment")}
        </Button>
      </PageHeader>

      <div className="flex flex-col sm:flex-row items-center gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" icon={ICONS.ChevronLeft} onClick={() => setDate((d) => addDays(d, -1))} />
          <input
            type="date"
            value={dateToISODate(date)}
            onChange={(e) => e.target.value && setDate(new Date(`${e.target.value}T00:00:00`))}
            className="text-sm px-3 py-2 rounded-xl border border-gray-100 bg-gray-50/50 focus:outline-none font-bold text-gray-700"
          />
          <Button variant="outline" size="sm" onClick={() => setDate(new Date())}>{t("btn_today", "Today")}</Button>
          <Button variant="outline" size="sm" className="rotate-180" icon={ICONS.ChevronLeft} onClick={() => setDate((d) => addDays(d, 1))} />
        </div>
        <div className="w-full sm:w-64">
          <SelectBox options={staffFilterOptions} value={staffFilter} onChange={setStaffFilter} label={t("filter_staff_label", "Staff")} />
        </div>
      </div>

      <p className="text-[11px] text-gray-300 font-medium mb-3 hidden sm:block">
        {t("drag_tip", "Tip: drag a booking to a new time or staff column to reschedule it")}
      </p>

      {staffList.length === 0 ? (
        <p className="text-center text-gray-400 italic py-16 bg-white rounded-[2rem] border border-gray-100">
          {t("no_staff_hint", "No staff can perform any active service yet — assign staff to a service first, from the Services screen.")}
        </p>
      ) : (
        <div className={`bg-white rounded-[2rem] border border-gray-100 shadow-sm overflow-x-auto transition-opacity ${loading ? "opacity-50 pointer-events-none" : ""}`}>
          <div className="flex min-w-max">
            {/* Time gutter */}
            <div className="w-16 shrink-0 border-e border-gray-50">
              <div className="h-12 border-b border-gray-50" />
              {slots.map((mins) => (
                <div key={mins} style={{ height: SLOT_HEIGHT }} className="text-[10px] text-gray-300 font-mono text-end pe-2 border-b border-gray-50">
                  {mins % 60 === 0 ? timeLabel(mins) : ""}
                </div>
              ))}
            </div>

            {visibleStaff.map((staff) => {
              const staffAppointments = appointmentsByStaff.get(staff.id) || [];
              return (
                <div key={staff.id} className="w-56 shrink-0 border-e border-gray-50 last:border-e-0">
                  <div className="h-12 flex items-center justify-center px-2 border-b border-gray-50 font-bold text-xs text-gray-700 truncate">
                    {staff.username}
                  </div>
                  <div
                    className="relative"
                    style={{ height: TOTAL_SLOTS * SLOT_HEIGHT }}
                    ref={(el) => { columnRefs.current[staff.id] = el; }}
                  >
                    {slots.map((mins) => (
                      <button
                        key={mins}
                        type="button"
                        onClick={() => openNewAppointment(staff.id, mins)}
                        style={{ height: SLOT_HEIGHT, top: ((mins - DAY_START_MINUTES) / SLOT_MINUTES) * SLOT_HEIGHT }}
                        className="absolute w-full border-b border-gray-50 hover:bg-[var(--primary-color)]/5 transition-colors"
                      />
                    ))}
                    {staffAppointments.map((a) => {
                      const start = new Date(a.start_time);
                      const end = new Date(a.end_time);
                      const startMins = start.getHours() * 60 + start.getMinutes();
                      const durMins = Math.max(15, (end - start) / 60000);
                      const top = ((startMins - DAY_START_MINUTES) / SLOT_MINUTES) * SLOT_HEIGHT;
                      const height = (durMins / SLOT_MINUTES) * SLOT_HEIGHT;
                      const colors = STATUS_COLORS[a.status] || STATUS_COLORS.scheduled;
                      const draggable = a.status === "scheduled" && canManage(a.user_id, a.branch_id);
                      const isBeingDragged = dragPreview?.appointmentId === a.id;
                      return (
                        <button
                          key={a.id}
                          type="button"
                          onMouseDown={draggable ? beginDrag(a) : undefined}
                          onClick={() => {
                            if (lastDragMovedRef.current) {
                              lastDragMovedRef.current = false;
                              return;
                            }
                            openEditAppointment(a);
                          }}
                          title={draggable ? t("drag_hint", "Drag to reschedule") : undefined}
                          style={{ top, height, insetInlineStart: 2, insetInlineEnd: 2 }}
                          className={`absolute rounded-lg border px-2 py-1 text-start overflow-hidden shadow-sm hover:shadow-md transition-shadow z-10 ${colors} ${draggable ? "cursor-grab active:cursor-grabbing" : ""} ${isBeingDragged ? "opacity-30" : ""}`}
                        >
                          <div className="text-[11px] font-bold truncate">{a.service?.name}</div>
                          <div className="text-[10px] truncate">{a.customer?.name}</div>
                        </button>
                      );
                    })}
                    {dragPreview && dragPreview.staffId === staff.id && (() => {
                      const previewStartMins = DAY_START_MINUTES + (dragPreview.top / SLOT_HEIGHT) * SLOT_MINUTES;
                      const previewEndMins = previewStartMins + (dragPreview.height / SLOT_HEIGHT) * SLOT_MINUTES;
                      return (
                        <div
                          style={{ top: dragPreview.top, height: dragPreview.height, insetInlineStart: 2, insetInlineEnd: 2 }}
                          className="absolute rounded-lg border-2 border-dashed border-[var(--primary-color)] bg-[var(--primary-color)]/10 px-2 py-1 text-start overflow-hidden z-20 pointer-events-none"
                        >
                          <div className="text-[11px] font-bold truncate text-[var(--primary-color)]">
                            {timeLabel(previewStartMins)} - {timeLabel(previewEndMins)}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {modal && (
        <AppointmentModal
          mode={modal.mode}
          appointment={modal.appointment}
          staffId={modal.staffId}
          startTime={modal.startTime}
          services={services}
          customers={customers}
          branches={branches}
          token={token}
          onClose={closeModal}
          onSaved={handleSaved}
          onDeleteClick={handleDeleteClick}
          canManage={canManage}
          t={t}
          tCommon={tCommon}
        />
      )}

      <ConfirmModal
        isOpen={deleteModal.isOpen}
        title={t("delete_confirm_title", "Delete Appointment")}
        message={t("delete_confirm_message", "Are you sure you want to delete this appointment? This can't be undone.")}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteModal({ isOpen: false, id: null })}
        isLoading={deleteStatus === "loading"}
        isSuccess={deleteStatus === "success"}
        isError={deleteStatus === "error"}
      />
    </div>
  );
}

// AppointmentModal handles BOTH creating a brand-new booking (mode="create",
// prefilled from whichever slot/staff column was clicked) and editing an
// existing one (mode="edit", including its Status field — the only place a
// booking can be cancelled/marked completed/marked no-show). Kept in this
// same file rather than split out, same convention Checkout.js's own
// SellVoucherModal already follows for a secondary modal that only this one
// page needs.
function AppointmentModal({ mode, appointment, staffId, startTime, services, customers, branches, token, onClose, onSaved, onDeleteClick, canManage, t, tCommon }) {
  const isEdit = mode === "edit";
  // A brand-new booking is always "manageable" by definition (its target
  // staff/branch aren't fixed yet — the Staff dropdown below is what
  // actually constrains who it can be for). Editing an EXISTING one is
  // read-only when canManage says no — a UX aid mirroring the backend's
  // own canManageAppointmentFor, so this only hides the Save/Delete
  // buttons; the real enforcement still happens server-side regardless.
  const canManageThis = !isEdit || canManage(appointment.user_id, appointment.branch_id);

  const [form, setForm] = useState(() => {
    if (isEdit && appointment) {
      const st = new Date(appointment.start_time);
      return {
        customer_id: String(appointment.customer_id),
        service_id: String(appointment.service_id),
        user_id: String(appointment.user_id),
        branch_id: appointment.branch_id ? String(appointment.branch_id) : "",
        date: dateToISODate(st),
        time: `${pad(st.getHours())}:${pad(st.getMinutes())}`,
        notes: appointment.notes || "",
        status: appointment.status,
      };
    }
    return {
      customer_id: "",
      service_id: "",
      user_id: staffId ? String(staffId) : "",
      branch_id: "",
      date: dateToISODate(startTime),
      time: `${pad(startTime.getHours())}:${pad(startTime.getMinutes())}`,
      notes: "",
      status: "scheduled",
    };
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const activeServices = services.filter((s) => s.is_active);
  const customerOptions = customers.filter((c) => c.is_active).map((c) => ({ value: String(c.id), label: c.phone ? `${c.name} — ${c.phone}` : c.name }));
  const serviceOptions = activeServices.map((s) => ({ value: String(s.id), label: `${s.name} (${t("duration_minutes_value", "{minutes} min").replace("{minutes}", s.duration_minutes)})` }));
  const selectedService = activeServices.find((s) => String(s.id) === form.service_id);
  // Only staff this caller is actually allowed to book/reassign FOR
  // (themselves, or their managed branch's staff, or anyone if
  // all_branches_access) show up here — same canManage the calendar's own
  // drag-and-drop is gated by, so a regular staff member can't fill out
  // the whole form only to have Save rejected by the backend's identical
  // check.
  const staffOptions = (selectedService?.staff_assignments || [])
    .filter((a) => a.is_active && a.user)
    .filter((a) => canManage(a.user.id, form.branch_id ? Number(form.branch_id) : null))
    .map((a) => ({ value: String(a.user.id), label: a.user.job_title ? `${a.user.username} — ${a.user.job_title}` : a.user.username }));
  const branchOptions = [{ value: "", label: t("branch_none_option", "No branch (unspecified)") }, ...branches.map((b) => ({ value: String(b.id), label: b.name }))];
  const statusOptions = [
    { value: "scheduled", label: t("status_scheduled", "Scheduled") },
    { value: "completed", label: t("status_completed", "Completed") },
    { value: "cancelled", label: t("status_cancelled", "Cancelled") },
    { value: "no_show", label: t("status_no_show", "No-show") },
  ];

  const setField = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  // Changing the service can invalidate whichever staff was picked — but
  // ONLY clear it when the current staff member truly can't perform the
  // newly-picked service, so clicking a specific staff column's slot and
  // then choosing a service that person actually performs doesn't force
  // re-picking them right back.
  const handleServiceChange = (val) => {
    const newService = activeServices.find((s) => String(s.id) === val);
    const validStaffIds = new Set((newService?.staff_assignments || []).filter((a) => a.is_active).map((a) => String(a.user_id)));
    setForm((prev) => ({ ...prev, service_id: val, user_id: validStaffIds.has(prev.user_id) ? prev.user_id : "" }));
    if (errors.service_id) setErrors((prev) => ({ ...prev, service_id: undefined }));
  };

  const handleSave = async () => {
    const newErrors = {};
    if (!form.customer_id) newErrors.customer_id = t("validation_customer_required", "Pick a customer");
    if (!form.service_id) newErrors.service_id = t("validation_service_required", "Pick a service");
    if (!form.user_id) newErrors.user_id = t("validation_staff_required", "Pick a staff member");
    if (!form.date || !form.time) newErrors.date = t("validation_datetime_required", "Pick a date and time");

    let startDate = null;
    if (form.date && form.time) {
      startDate = new Date(`${form.date}T${form.time}:00`);
      // A booking reserves FUTURE time — block moving one into the past,
      // but only when start_time is actually changing (or this is a
      // brand-new booking) AND it will still be "scheduled" afterward.
      // Editing an overdue scheduled appointment's notes, or closing it
      // out as completed/cancelled/no-show, must stay possible without
      // touching start_time at all — same gating as the backend's own
      // check (UpdateAppointmentHandler), so neither side ever disagrees
      // with the other about what's blocked.
      const willBeScheduled = !isEdit || form.status === "scheduled";
      const timeUnchanged = isEdit && appointment && startDate.getTime() === new Date(appointment.start_time).getTime();
      if (willBeScheduled && !timeUnchanged && startDate.getTime() < Date.now()) {
        newErrors.date = t("validation_no_past_booking", "Cannot book an appointment in the past");
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      notify.error(tCommon("validation_fill_required_fields", "Please fill in the required fields correctly"));
      return;
    }

    const payload = {
      customer_id: Number(form.customer_id),
      service_id: Number(form.service_id),
      user_id: Number(form.user_id),
      branch_id: form.branch_id ? Number(form.branch_id) : 0,
      start_time: toRFC3339Local(startDate),
      notes: form.notes.trim(),
      status: form.status,
    };

    setSaving(true);
    try {
      if (isEdit) {
        await updateAppointment(token, appointment.id, payload);
        notify.success(t("notify_updated", "Appointment updated"));
      } else {
        await createAppointment(token, payload);
        notify.success(t("notify_created", "Appointment booked"));
      }
      onSaved();
    } catch (err) {
      notify.error(err.data?.error || err.message || tCommon("error_generic", "Something went wrong"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-[2rem] p-6 w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-black text-lg text-gray-800">{isEdit ? t("modal_edit_title", "Appointment") : t("modal_new_title", "New Appointment")}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <ICONS.Close className="w-6 h-6" />
          </button>
        </div>

        {!canManageThis && (
          <p className="text-[11px] text-amber-600 font-bold mb-3 text-center bg-amber-50 rounded-xl py-2">
            {t("view_only_hint", "View only — you don't manage this staff member's appointments")}
          </p>
        )}

        <div className={`space-y-4 ${!canManageThis ? "opacity-60 pointer-events-none select-none" : ""}`}>
          <div>
            <SelectBox label={t("field_customer_label", "Customer")} options={customerOptions} value={form.customer_id} onChange={setField("customer_id")} />
            {errors.customer_id && <p className="text-[10px] text-red-500 font-bold mt-1 ms-2 uppercase tracking-tight">{errors.customer_id}</p>}
          </div>

          <div>
            <SelectBox label={t("field_service_label", "Service")} options={serviceOptions} value={form.service_id} onChange={handleServiceChange} />
            {errors.service_id && <p className="text-[10px] text-red-500 font-bold mt-1 ms-2 uppercase tracking-tight">{errors.service_id}</p>}
          </div>

          <div>
            <SelectBox label={t("field_staff_label", "Staff Member")} options={staffOptions} value={form.user_id} onChange={setField("user_id")} isDisabled={!form.service_id} />
            {errors.user_id && <p className="text-[10px] text-red-500 font-bold mt-1 ms-2 uppercase tracking-tight">{errors.user_id}</p>}
            {form.service_id && staffOptions.length === 0 && (
              <p className="text-[10px] text-amber-500 font-bold mt-1 ms-2">{t("no_staff_for_service_hint", "No staff assigned to this service yet")}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            {/* min is just a UX nudge (native date pickers grey out earlier
                days) — the real enforcement is handleSave's own
                past-booking check above plus the backend's, so this never
                needs to be perfectly in sync with either. */}
            <FloatingInput label={t("field_date_label", "Date")} type="date" value={form.date} onChange={setField("date")} error={errors.date} min={dateToISODate(new Date())} />
            <FloatingInput label={t("field_time_label", "Time")} type="time" value={form.time} onChange={setField("time")} />
          </div>

          {branches.length > 0 && (
            <SelectBox label={t("field_branch_label", "Branch (optional)")} options={branchOptions} value={form.branch_id} onChange={setField("branch_id")} />
          )}

          {isEdit && (
            <SelectBox label={t("field_status_label", "Status")} options={statusOptions} value={form.status} onChange={setField("status")} />
          )}

          <FloatingInput label={t("field_notes_label", "Notes (optional)")} value={form.notes} onChange={setField("notes")} />
        </div>

        <div className="flex gap-3 pt-6 mt-2 border-t border-gray-50">
          {canManageThis && (
            <>
              <Button variant="primary" onClick={handleSave} loading={saving} className="flex-grow">
                {isEdit ? tCommon("btn_save", "Save") : t("btn_book", "Book Appointment")}
              </Button>
              {isEdit && <Button variant="danger" icon={ICONS.DeleteUser} onClick={() => onDeleteClick(appointment.id)} />}
            </>
          )}
          <Button variant="secondary" onClick={onClose} className={!canManageThis ? "flex-grow" : ""}>{tCommon("btn_cancel", "Cancel")}</Button>
        </div>
      </div>
    </div>
  );
}
